import {Component, inject, OnInit} from '@angular/core';
import {CmsApiService} from "../../../core/cms-api.service";
import {FormControl} from "@angular/forms";
import {LoggerService} from '../../../core/logger.service';
import {saveAs} from 'file-saver';
import {
  apiFilterQuery,
  groupServiceEndpoints,
  readablePathTitle,
  restoredSelectionKey,
  serviceSelectionKey,
  ServiceListGroup
} from './admin-service-api-list';

interface ApiService {
  path: string;
  method: string;
  content_type: string;
  title: string;
  parameters?: ApiParameter[];
  skip_api_path: boolean;
  stream: boolean;
}

interface ApiParameter {
  name: string;
  param_type: string;
  title: string;
  formControl: FormControl;
}

@Component({
    selector: 'app-admin-service-api',
    templateUrl: './admin-service-api.component.html',
    styleUrls: ['./admin-service-api.component.scss'],
    standalone: false
})
export class AdminServiceApiComponent implements OnInit {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly logger: LoggerService = inject(LoggerService);

  serviceRegister: {end_points: ApiService[]} = {
    end_points: []
  };
  apiQuery = new FormControl('', {nonNullable: true});
  selectedService?: ApiService;
  readonly displayApi = (value: string | null): string => {
    if (!value) {
      return '';
    }
    const selected = this.serviceRegister.end_points.find(endpoint =>
      serviceSelectionKey(endpoint.method, endpoint.path) === value);
    if (!selected) {
      return value;
    }
    return `${selected.method} ${readablePathTitle(selected.path)}`;
  };
  registerLoaded = false;
  executeResult: any;
  errorMessage = '';
  executing = false;
  executeTime = 0;

  private isZipDownloadEndpoint(path: string): boolean {
    return path === 'multimedia/test_media/generate_zip';
  }

  private buildZipFilename(params: any): string {
    const baseId = String(params?.base_id ?? 'test-media').replace(/[^A-Za-z0-9._-]+/g, '_');
    const mediaType = String(params?.media_type ?? 'media').replace(/[^A-Za-z0-9._-]+/g, '_');
    const count = Number.isFinite(params?.count) ? String(params.count) : 'files';
    return `${baseId}_${mediaType}_${count}.zip`;
  }

  ngOnInit(): void {
    this.cms.getServiceRegister().then(res => {
      this.serviceRegister = res;
      this.registerLoaded = true;
    });
  }

  get hasQueryText(): boolean {
    return this.apiQuery.value.trim().length > 0;
  }

  get groups(): ServiceListGroup<ApiService>[] {
    return groupServiceEndpoints(
      this.serviceRegister.end_points,
      apiFilterQuery(this.apiQuery.value, this.serviceRegister.end_points));
  }

  setService(key: string): void {
    if (typeof key !== 'string' || key.length === 0) {
      return;
    }
    const selected = this.serviceRegister.end_points.find(endPoint =>
      serviceSelectionKey(endPoint.method, endPoint.path) === key);
    if (!selected) {
      return;
    }
    this.selectedService = selected;
    for (const parameter of selected.parameters ?? []) {
      if (!parameter.formControl) {
        parameter.formControl = new FormControl<any>('');
      }
    }
  }

  clearQuery(): void {
    this.selectedService = undefined;
    this.apiQuery.setValue('');
  }

  onApiInput(): void {
    if (this.apiQuery.value === '') {
      this.selectedService = undefined;
    }
  }

  restoreSelectedApi(): void {
    const keyAtBlur = this.selectionKey();
    setTimeout(() => {
      const key = restoredSelectionKey(keyAtBlur, this.selectionKey(), this.apiQuery.value);
      if (key) {
        this.apiQuery.setValue(key);
      }
    });
  }

  private selectionKey(): string | null {
    if (!this.selectedService) {
      return null;
    }
    return serviceSelectionKey(this.selectedService.method, this.selectedService.path);
  }

  async executeService() {
    let path = this.selectedService.path;
    let outParams = {};
    let addPrefix = '/';
    this.errorMessage = '';
    for (const parameter of this.selectedService.parameters || []) {
      const value = parameter.formControl.value;
      if (!value) {
        continue
      }
      switch (parameter.param_type) {
        case 'add_to_path':
          path += addPrefix + value;
          addPrefix = ','
          break;
        case 'string':
        case 'boolean':
          outParams[parameter.name] = value;
          break;
        case 'number':
          outParams[parameter.name] = Number(value);
          break
        case 'list':
          outParams[parameter.name] = value.split(',');
          break;
        case 'object':
          try {
            outParams[parameter.name] = JSON.parse(value)
          } catch (e) {
            this.errorMessage = e.message;
            return;
          }
          break;
        default:
          this.logger.warn(`Unknown type ${parameter.param_type}`)
      }
    }
    try {
      this.executing = true;
      const startTime = Date.now();
      if (!this.selectedService.stream) {
        const expectZipDownload = this.isZipDownloadEndpoint(this.selectedService.path);
        const response = await this.cms.executeService(
          path, outParams, this.selectedService.method, this.selectedService.content_type,
          this.selectedService.skip_api_path, false,
          expectZipDownload ? 'application/zip' : undefined);

        if (expectZipDownload && response instanceof Blob) {
          const fileName = this.buildZipFilename(outParams);
          saveAs(response, fileName);
          this.executeResult = {
            message: 'Download started',
            filename: fileName,
            size_bytes: response.size
          };
        } else {
          this.executeResult = response;
        }
      } else {
        this.executeResult = '';
        this.cms.executeService(path, outParams, this.selectedService.method, this.selectedService.content_type,
          this.selectedService.skip_api_path, true).then(reader => {
          const readChunk = () => {
            reader.read().then(({done, value}) => {
              if (done) return;
              const chunk = value.toString();
              try {
                const data = JSON.parse(chunk).trim();
                this.executeResult += data.dmsid;
              } catch (e) {
                this.errorMessage = e.message;
              }
              readChunk();
            });
          };
          readChunk();
        });
      }
      const endTime = Date.now();
      this.executeTime = endTime - startTime;
      this.executing = false;
    } catch (e) {
      this.logger.error(`Exception occurred executing ${path}: ` + e.message);
      this.errorMessage = e.message;
      this.executing = false;
    }
  }

}
